import { Navigate, RouteObject } from "react-router-dom";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { useIsFeatureFlagEnabled } from "../featureFlags/useIsFeatureFlagEnabled";

const getBackOfficeRoutes = (
  backOfficeNavigationHomeEnabled: boolean,
  backOfficeNavigationBillingEnabled: boolean,
  backOfficeNavigationPricingRatingEnabled: boolean,
  backOfficeNavigationSalesEnabled: boolean,
  backOfficeQuickQuoteEnabled: boolean,
  backOfficeRateQuoteEnabled: boolean,
  backOfficeOrderEntryEnabled: boolean,
  backOfficePickupReviewEnabled: boolean,
  backOfficeClientActivityEnabled: boolean,
  backOfficeClientActivityShipmentsEnabled: boolean,
  backOfficeClientActivityQuotesEnabled: boolean,
  backOfficeGlobalRatesNavigationEnabled: boolean,
  backOfficeFuelSurchargeNavigationEnabled: boolean,
  backOfficeLaneMinimumNavigationEnabled: boolean,
  backOfficeBillingDashboardEnabled: boolean,
  backOfficeBillingQueueEnabled: boolean,
  backOfficeAccessorialNavigationEnabled: boolean,
  backOfficeRulesNavigationEnabled: boolean,
  backOfficeHighCostZipCodesNavigationEnabled: boolean,
  backOfficeChangeRequestsEnabled: boolean,
  backOfficeExceptionsEnabled: boolean,
  backOfficeRateBasesNavigationEnabled: boolean,
  backOfficeNavigationTransitTimesEnabled: boolean,
  backOfficeTransitTimeEolServiceCentersNavigationEnabled: boolean
): RouteObject[] => [
  {
    path: "back-office",
    lazy: () => import("../pages/backOffice/BackOfficeLayout"),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="clients" replace />,
      },
      ...(backOfficeNavigationHomeEnabled
        ? [
            {
              path: "home",
              lazy: () => import("../pages/backOffice/home/Home"),
              errorElement: <ErrorBoundary />,
            },
          ]
        : []),
      ...(backOfficeNavigationBillingEnabled
        ? [
            {
              path: "billing",
              lazy: () => import("../pages/backOffice/billing/Billing"),
              errorElement: <ErrorBoundary />,
              children: [
                {
                  index: true,
                  element: <Navigate to="billing-dashboard" replace />,
                },
                ...(backOfficeBillingDashboardEnabled
                  ? [
                      {
                        path: "billing-dashboard",
                        lazy: () =>
                          import(
                            "../pages/backOffice/billing/billingDashboard/BillingDashboard"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
                ...(backOfficeBillingQueueEnabled
                  ? [
                      {
                        path: "billing-queue",
                        lazy: () =>
                          import(
                            "../pages/backOffice/billing/billingQueue/BillingQueue"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
                {
                  path: "billing-entry/:id",
                  lazy: () =>
                    import(
                      "../pages/backOffice/billing/billingEntry/BillingEntry"
                    ),
                  errorElement: <ErrorBoundary />,
                },
                ...(backOfficeExceptionsEnabled
                  ? [
                      {
                        path: "exceptions",
                        lazy: () =>
                          import(
                            "../pages/backOffice/billing/exceptions/Exceptions"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
                ...(backOfficeChangeRequestsEnabled
                  ? [
                      {
                        path: "change-requests",
                        lazy: () =>
                          import(
                            "../pages/backOffice/billing/changeRequests/ChangeRequests"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      {
        path: "clients",
        lazy: () => import("../pages/backOffice/clients/ClientsLayout"),
        errorElement: <ErrorBoundary />,
        children: [
          {
            index: true,
            element: <Navigate to="dashboard" replace />,
          },
          {
            path: "dashboard",
            lazy: () =>
              import("../pages/backOffice/clients/dashboard/Dashboard"),
            errorElement: <ErrorBoundary />,
          },
          ...(backOfficeOrderEntryEnabled
            ? [
                {
                  path: "pickup-entry",
                  lazy: () =>
                    import(
                      "../pages/backOffice/clients/pickupEntry/PickupEntry"
                    ),
                  errorElement: <ErrorBoundary />,
                },
                {
                  path: "pickup-entry/review",
                  lazy: () =>
                    import(
                      "../pages/backOffice/clients/pickupEntry/PickupEntryReview"
                    ),
                  errorElement: <ErrorBoundary />,
                },

                {
                  path: "pickup-entry/summary",
                  lazy: () =>
                    import(
                      "../pages/backOffice/clients/pickupEntry/PickupEntrySummary"
                    ),
                  errorElement: <ErrorBoundary />,
                },
              ]
            : []),
          ...(backOfficePickupReviewEnabled
            ? [
                {
                  path: "pickup-review",
                  lazy: () =>
                    import(
                      "../pages/backOffice/clients/pickupReview/PickupReviewPage"
                    ),
                  errorElement: <ErrorBoundary />,
                },
                {
                  path: "pickup-review/:pickupId",
                  lazy: () =>
                    import(
                      "../pages/backOffice/clients/pickupReview/PickupReviewDetailPage"
                    ),
                  errorElement: <ErrorBoundary />,
                },
              ]
            : []),
          ...(backOfficeQuickQuoteEnabled
            ? [
                {
                  path: "quick-quote",
                  lazy: () =>
                    import(
                      "../pages/backOffice/clients/quickQuote/QuickQuoteLayout"
                    ),
                  errorElement: <ErrorBoundary />,
                },
                {
                  path: "quick-quote/summary/:guaranteedServiceId",
                  lazy: () =>
                    import(
                      "../pages/backOffice/clients/quickQuote/QuickQuoteSummaryLayout"
                    ),
                  errorElement: <ErrorBoundary />,
                },
              ]
            : []),
          ...(backOfficeRateQuoteEnabled
            ? [
                {
                  path: "rate-quote",
                  lazy: () =>
                    import(
                      "../pages/backOffice/clients/rateQuote/RateQuoteLayout"
                    ),
                  errorElement: <ErrorBoundary />,
                },
                {
                  path: "rate-quote/summary",
                  lazy: () =>
                    import(
                      "../pages/backOffice/clients/rateQuote/RateQuoteSummary"
                    ),
                  errorElement: <ErrorBoundary />,
                },
              ]
            : []),
          ...(backOfficeClientActivityEnabled
            ? [
                {
                  path: "shipment-activity",
                  lazy: () =>
                    import(
                      "../pages/backOffice/clients/clientActivity/ClientActivity"
                    ),
                  errorElement: <ErrorBoundary />,
                  children: [
                    {
                      index: true,
                      element: (
                        <Navigate
                          to={
                            backOfficeClientActivityShipmentsEnabled
                              ? "shipments"
                              : "quotes"
                          }
                          replace
                        />
                      ),
                    },
                    ...(backOfficeClientActivityShipmentsEnabled
                      ? [
                          {
                            path: "shipments",
                            lazy: () =>
                              import(
                                "../pages/backOffice/clients/clientActivity/shipments/Shipments"
                              ),
                            errorElement: <ErrorBoundary />,
                          },
                        ]
                      : []),
                    ...(backOfficeClientActivityQuotesEnabled
                      ? [
                          {
                            path: "quotes",
                            lazy: () =>
                              import(
                                "../pages/backOffice/clients/clientActivity/quotes/Quotes"
                              ),
                            errorElement: <ErrorBoundary />,
                          },
                        ]
                      : []),
                  ],
                },
              ]
            : []),
        ],
      },
      ...(backOfficeNavigationPricingRatingEnabled
        ? [
            {
              path: "pricing-and-rating",
              lazy: () =>
                import(
                  "../pages/backOffice/pricingAndRating/PricingAndRatingLayout"
                ),
              errorElement: <ErrorBoundary />,
              children: [
                {
                  index: true,
                  element: (
                    <Navigate
                      to={
                        backOfficeGlobalRatesNavigationEnabled
                          ? "global-rates"
                          : backOfficeRateBasesNavigationEnabled
                            ? "rate-bases"
                            : backOfficeFuelSurchargeNavigationEnabled
                              ? "fuel-surcharge"
                              : backOfficeAccessorialNavigationEnabled
                                ? "accessorial-charges"
                                : backOfficeLaneMinimumNavigationEnabled
                                  ? "lane-minimums"
                                  : backOfficeHighCostZipCodesNavigationEnabled
                                    ? "high-cost-zip-codes"
                                    : "rules"
                      }
                      replace
                    />
                  ),
                },
                ...(backOfficeGlobalRatesNavigationEnabled
                  ? [
                      {
                        path: "global-rates",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/globalRates/GlobalRates"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
                // BOD-965: Rate Bases navigation
                ...(backOfficeRateBasesNavigationEnabled
                  ? [
                      {
                        path: "rate-bases",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/rateBases/RateBases"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
                ...(backOfficeFuelSurchargeNavigationEnabled
                  ? [
                      {
                        path: "fuel-surcharge",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/fuelSurcharge/FuelSurcharge"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "fuel-surcharge/fuel-scale/add-new",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/fuelSurcharge/fuelScale/AddNewCode"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "fuel-surcharge/edit/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/fuelSurcharge/fuelScale/AddNewCode"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "fuel-surcharge/fuel-scale/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/fuelSurcharge/fuelScale/FuelScaleDetail"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
                ...(backOfficeAccessorialNavigationEnabled
                  ? [
                      {
                        path: "accessorial-charges",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/accessorialCharges/AccessorialChargesLayout"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "accessorial-charges/add-new",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/accessorialCharges/AddNewAccessorialCharge"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "accessorial-charges/edit/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/accessorialCharges/AddNewAccessorialCharge"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "accessorial-charges/duplicate/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/accessorialCharges/AddNewAccessorialCharge"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "accessorial-charges/add-new-schedule",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/accessorialCharges/AddNewAccessorialSchedule"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "accessorial-charges/edit-schedule/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/accessorialCharges/AddNewAccessorialSchedule"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "accessorial-charges/schedule/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/accessorialCharges/AccessorialScheduleView"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "accessorial-charges/view/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/accessorialCharges/AccessorialChargeView"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
                ...(backOfficeLaneMinimumNavigationEnabled
                  ? [
                      {
                        path: "lane-minimums",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/laneMinimum/LaneMinimum"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "lane-minimums/add-new-schedule",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/laneMinimum/AddNewLaneMinimumSchedule"
                          ),
                        errorElement: <ErrorBoundary />,
                      },

                      {
                        path: "lane-minimums/edit-schedule/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/laneMinimum/AddNewLaneMinimumSchedule"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "lane-minimums/duplicate-schedule/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/laneMinimum/AddNewLaneMinimumSchedule"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
                ...(backOfficeHighCostZipCodesNavigationEnabled
                  ? [
                      {
                        path: "high-cost-zip-codes",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/highCostZipCodes/HighCostZipCodes"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "high-cost-zip-codes/add-new-schedule",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/highCostZipCodes/AddNewHighCostZipCodeSchedule"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "high-cost-zip-codes/edit-schedule/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/highCostZipCodes/AddNewHighCostZipCodeSchedule"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                      {
                        path: "high-cost-zip-codes/duplicate-schedule/:id",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/highCostZipCodes/AddNewHighCostZipCodeSchedule"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
                // BOD-861: Rules navigation
                ...(backOfficeRulesNavigationEnabled
                  ? [
                      {
                        path: "rules",
                        lazy: () =>
                          import(
                            "../pages/backOffice/pricingAndRating/rules/Rules"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(backOfficeNavigationSalesEnabled
        ? [
            {
              path: "sales",
              lazy: () => import("../pages/backOffice/sales/Sales"),
              errorElement: <ErrorBoundary />,
            },
          ]
        : []),
      ...(backOfficeNavigationTransitTimesEnabled
        ? [
            {
              path: "transit-times",
              lazy: () =>
                import("../pages/backOffice/transitTimes/TransitTimesLayout"),
              errorElement: <ErrorBoundary />,
              children: [
                {
                  index: true,
                  element: <Navigate to="eol-service-centres" replace />,
                },
                ...(backOfficeTransitTimeEolServiceCentersNavigationEnabled
                  ? [
                      {
                        path: "eol-service-centres",
                        lazy: () =>
                          import(
                            "../pages/backOffice/transitTimes/endOfLineServiceCenters/EolServiceCentersList"
                          ),
                        errorElement: <ErrorBoundary />,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
    ],
  },
];

export const useBackOfficeRoutes = (): RouteObject[] => {
  const backOfficeNavigationHomeEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-198-navigation-home-client"
  );
  const backOfficeNavigationBillingEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-199-navigation-billing-client"
  );
  const backOfficeNavigationPricingRatingEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-201-navigation-pricing-rating-client"
  );
  const backOfficeNavigationSalesEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-202-navigation-sales-client"
  );
  const backOfficeQuickQuoteV1Enabled = useIsFeatureFlagEnabled(
    "back-office-bod-196-quick-quote-client"
  );
  const backOfficeQuickQuoteV2Enabled = useIsFeatureFlagEnabled(
    "back-office-bod-83-quick-quote-v2-client"
  );
  const backOfficeQuickQuoteEnabled =
    backOfficeQuickQuoteV1Enabled || backOfficeQuickQuoteV2Enabled;
  const backOfficeRateQuoteV1Enabled = useIsFeatureFlagEnabled(
    "back-office-bod-195-rate-quote-client"
  );
  const backOfficeRateQuoteV2Enabled = useIsFeatureFlagEnabled(
    "back-office-bod-367-rate-quote-v2-client"
  );
  const backOfficeRateQuoteEnabled =
    backOfficeRateQuoteV1Enabled || backOfficeRateQuoteV2Enabled;
  const backOfficeOrderEntryEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-203-order-entry-client"
  );
  const backOfficePickupReviewEnabled = useIsFeatureFlagEnabled(
    "back-office-cs-279-pickup-review-navigation-client"
  );
  const backOfficeClientActivityEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-231-client-activity-client"
  );
  const backOfficeClientActivityShipmentsEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-233-client-activity-shipments-client"
  );
  const backOfficeClientActivityQuotesEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-429-client-activity-quote-client"
  );
  const backOfficeGlobalRatesNavigationEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-614-global-rates-navigation-client"
  );
  const backOfficeFuelSurchargeNavigationEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-435-fuel-surcharge-navigation-client"
  );
  const backOfficeLaneMinimumNavigationEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-771-lane-minimum-navigation-client"
  );
  const backOfficeBillingDashboardEnabled = useIsFeatureFlagEnabled(
    "back-office-bil-88-billing-dashboard-navigation-client"
  );
  const backOfficeBillingQueueEnabled = useIsFeatureFlagEnabled(
    "back-office-bil-95-billing-queue-navigation-client"
  );
  const backOfficeChangeRequestsEnabled = useIsFeatureFlagEnabled(
    "back-office-bil-149-billing-change-requests-navigation-client"
  );
  const backOfficeAccessorialNavigationEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-725-accessorial-navigation-client"
  );
  const backOfficeRulesNavigationEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-861-rules-navigation-client"
  );
  const backOfficeHighCostZipCodesNavigationEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-791-high-cost-zipcodes-navigation-client"
  );
  const backOfficeRateBasesNavigationEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-965-rate-base-navigation-client"
  );
  const backOfficeExceptionsEnabled = useIsFeatureFlagEnabled(
    "back-office-bil-153-billing-exceptions-navigation-client"
  );
  const backOfficeNavigationTransitTimesEnabled = useIsFeatureFlagEnabled(
    "back-office-ttsl-169-navigation-transit-times-client"
  );
  const backOfficeTransitTimeEolServiceCentersNavigationEnabled =
    useIsFeatureFlagEnabled(
      "back-office-ttsl-92-transit-time-eol-service-centers-navigation-client"
    );

  return getBackOfficeRoutes(
    backOfficeNavigationHomeEnabled,
    backOfficeNavigationBillingEnabled,
    backOfficeNavigationPricingRatingEnabled,
    backOfficeNavigationSalesEnabled,
    backOfficeQuickQuoteEnabled,
    backOfficeRateQuoteEnabled,
    backOfficeOrderEntryEnabled,
    backOfficePickupReviewEnabled,
    backOfficeClientActivityEnabled,
    backOfficeClientActivityShipmentsEnabled,
    backOfficeClientActivityQuotesEnabled,
    backOfficeGlobalRatesNavigationEnabled,
    backOfficeFuelSurchargeNavigationEnabled,
    backOfficeLaneMinimumNavigationEnabled,
    backOfficeBillingDashboardEnabled,
    backOfficeBillingQueueEnabled,
    backOfficeExceptionsEnabled,
    backOfficeChangeRequestsEnabled,
    backOfficeAccessorialNavigationEnabled,
    backOfficeRulesNavigationEnabled,
    backOfficeHighCostZipCodesNavigationEnabled,
    backOfficeRateBasesNavigationEnabled,
    backOfficeNavigationTransitTimesEnabled,
    backOfficeTransitTimeEolServiceCentersNavigationEnabled
  );
};
